// R1b-β Step 4 Deliverable B — Tenants directory with optional Platform
// Operator cross-tenant view. Owners still see and edit their OWN tenant
// only. Platform Operators additionally see the "All Tenants" cross-tenant
// list at the top (server-gated on `require_platform_operator`).
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import { Building2, Pause, Play, Users as UsersIcon, ShieldCheck } from "lucide-react";

function AllTenantsPanel() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // {tenant, mode: 'suspend'|'restore'}
  const [reason, setReason] = useState("");

  const load = () => api.get("/tenants/all")
    .then((r) => setRows(r.data))
    .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (reason.length < 6) { toast.error("Reason must be at least 6 characters."); return; }
    const path = modal.mode === "suspend"
      ? `/tenants/${modal.tenant.id}/suspend`
      : `/tenants/${modal.tenant.id}/restore`;
    setBusy(true);
    try {
      await api.post(path, { reason });
      toast.success(`Tenant ${modal.mode}ed — audit-logged`);
      setModal(null); setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (err) return (
    <Card className="border-erf-borderCream" data-testid="all-tenants-error">
      <CardContent className="p-5 text-xs font-mono text-red-700">{err}</CardContent>
    </Card>
  );

  return (
    <Card className="border-erf-borderCream bg-white" data-testid="all-tenants-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Cross-Tenant Administration</div>
            <div className="font-display font-semibold text-erf-dark text-lg mt-0.5">All Tenants</div>
            <p className="text-xs font-nav text-erf-dark/70 mt-1">
              Platform Operator view. SaaS-scope only — no business-sensitive data.
            </p>
          </div>
          <StatusChip status="connected" label="Platform Operator" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full erf-table text-xs">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Legal name</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Users</th>
                <th className="text-left px-3 py-2">Projects</th>
                <th className="text-left px-3 py-2">Paywall</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={7} className="px-3 py-4 font-mono text-erf-dark/50">Loading…</td></tr>
              )}
              {rows && rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-4 font-mono text-erf-dark/50">No tenants.</td></tr>
              )}
              {rows && rows.map((t) => (
                <tr key={t.id} className="border-t border-erf-borderCream/60"
                    data-testid={`all-tenants-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-nav text-erf-dark">{t.legal_name || t.display_name || "—"}</div>
                    <div className="font-mono text-[10px] text-erf-dark/50">{t.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-erf-dark/70">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono">{t.user_count}</td>
                  <td className="px-3 py-2 font-mono">{t.project_count_estimate}</td>
                  <td className="px-3 py-2">
                    {t.paywall_immune
                      ? <StatusChip status="connected" label="Immune" />
                      : <StatusChip status="phase_n" label="Standard" />}
                  </td>
                  <td className="px-3 py-2">
                    {t.suspended
                      ? <StatusChip status="error" label="Suspended" />
                      : <StatusChip status="connected" label="Active" />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.suspended ? (
                      <Button size="sm" variant="ghost" className="text-emerald-700 h-8"
                              onClick={() => { setModal({ tenant: t, mode: "restore" }); setReason(""); }}
                              data-testid={`all-tenants-restore-${t.id}`}
                              title="Restore tenant (audit-logged, elevated severity)">
                        <Play className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-red-700 h-8"
                              disabled={t.paywall_immune}
                              onClick={() => { setModal({ tenant: t, mode: "suspend" }); setReason(""); }}
                              data-testid={`all-tenants-suspend-${t.id}`}
                              title={t.paywall_immune ? "Immune tenants cannot be suspended from this UI" : "Suspend tenant (audit-logged, elevated severity)"}>
                        <Pause className="w-3.5 h-3.5 mr-1" /> Suspend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
          <DialogContent className="bg-erf-cream max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display" data-testid="all-tenants-modal-title">
                {modal?.mode === "suspend" ? "Suspend tenant" : "Restore tenant"}
              </DialogTitle>
            </DialogHeader>
            {modal && (
              <div className="space-y-3">
                <div className="text-xs font-mono text-erf-dark/70">
                  Tenant · {modal.tenant.legal_name || modal.tenant.id}
                </div>
                <Textarea
                  placeholder="Reason (≥ 6 chars, audit-logged)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  data-testid="all-tenants-reason"
                />
                <Button
                  disabled={busy || reason.length < 6}
                  onClick={submit}
                  className={modal.mode === "suspend" ? "bg-red-700 text-white hover:bg-red-800" : "bg-emerald-700 text-white hover:bg-emerald-800"}
                  data-testid="all-tenants-confirm"
                >
                  {busy ? "Recording…" : (modal.mode === "suspend" ? "Suspend (audit-logged)" : "Restore (audit-logged)")}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function TenantsAdmin() {
  const { user } = useAuth();
  const isPlatformOperator = user?.role_key === "PLATFORM_OPERATOR";
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";

  const [tenant, setTenant] = useState(null);
  const [display, setDisplay] = useState("");
  const [states, setStates] = useState("");
  const [compliance, setCompliance] = useState({ primary_state: "PA", hicpa_number: "", governing_law_state: "PA", governing_law_county: "Chester", record_retention_years: 7 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api.get("/tenants/me").then((r) => {
      setTenant(r.data);
      setDisplay(r.data.display_name || "");
      setStates((r.data.states_of_operation || []).join(", "));
      setCompliance((c) => ({ ...c, ...(r.data.compliance || {}) }));
    });
  }, [isOwner]);

  const save = async () => {
    setBusy(true);
    try {
      const upd = {
        display_name: display,
        states_of_operation: states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        compliance: { ...compliance, record_retention_years: parseInt(compliance.record_retention_years || 7, 10) },
      };
      const r = await api.patch("/tenants/me", upd);
      setTenant(r.data);
      toast.success("Tenant updated");
    } catch (e) {
      toast.error("Update failed");
    } finally { setBusy(false); }
  };

  return (
    <AdminAppShell
      activeNav="people-access"
      title="Tenants Directory"
      subtitle={isPlatformOperator ? "Cross-tenant administration · SaaS scope" : "Multi-tenant separation · self-tenant only in Phase 1"}
      breadcrumbs={[
        { label: "People & Access", to: "/admin/people-roles/users" },
        { label: "Tenants" },
      ]}
      user={user}
    >
      <div className="mx-auto w-full max-w-[1600px] box-border px-4 md:px-6 lg:px-8 space-y-6"
           data-testid="tenants-page">
        {isPlatformOperator && <AllTenantsPanel />}

        {isOwner && tenant && (
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2 border-erf-borderCream bg-erf-cream">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-erf-gold mt-1" aria-hidden="true" />
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">My Tenant</div>
                    <div className="font-display font-semibold text-erf-dark">Company profile</div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav text-erf-dark/70">Legal name</Label>
                  <div className="mono text-sm mt-1">{tenant.legal_name}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav text-erf-dark/70">Display name</Label>
                  <Input value={display} onChange={(e) => setDisplay(e.target.value)} data-testid="tenant-display-input" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav text-erf-dark/70">States of operation (comma-separated)</Label>
                  <Input value={states} onChange={(e) => setStates(e.target.value)} data-testid="tenant-states-input" className="mt-1 font-mono" />
                </div>

                <div className="border-t border-erf-borderCream pt-4">
                  <div className="text-xs uppercase font-nav text-erf-dark/70 mb-2">Compliance (Phase 6 spec — data captured now)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase font-nav">Primary state</Label>
                      <Input value={compliance.primary_state || ""} onChange={(e)=>setCompliance({...compliance, primary_state: e.target.value.toUpperCase()})} className="mt-1 font-mono" data-testid="compliance-primary-state" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase font-nav">HICPA number (PA)</Label>
                      <Input value={compliance.hicpa_number || ""} onChange={(e)=>setCompliance({...compliance, hicpa_number: e.target.value})} className="mt-1 font-mono" placeholder="PA######" data-testid="compliance-hicpa" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase font-nav">Governing law · state</Label>
                      <Input value={compliance.governing_law_state || ""} onChange={(e)=>setCompliance({...compliance, governing_law_state: e.target.value.toUpperCase()})} className="mt-1 font-mono" data-testid="compliance-law-state" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase font-nav">Governing law · county</Label>
                      <Input value={compliance.governing_law_county || ""} onChange={(e)=>setCompliance({...compliance, governing_law_county: e.target.value})} className="mt-1" data-testid="compliance-law-county" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase font-nav">Record retention (years)</Label>
                      <Input type="number" value={compliance.record_retention_years || 7} onChange={(e)=>setCompliance({...compliance, record_retention_years: e.target.value})} className="mt-1 font-mono" data-testid="compliance-retention-years" />
                    </div>
                  </div>
                </div>

                <Button onClick={save} disabled={busy} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="tenant-save-btn">
                  {busy ? "Saving…" : "Save changes"}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-erf-borderCream bg-erf-cream">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-erf-gold" aria-hidden="true" />
                  <div className="font-display font-semibold text-erf-dark">Business scope</div>
                </div>
                <div className="text-sm font-nav text-erf-dark">{tenant.business_type?.replaceAll("_", " ")}</div>
                <div className="text-xs uppercase font-nav text-erf-dark/70 pt-2">Sectors served</div>
                <div className="flex flex-wrap gap-1">
                  {(tenant.sectors_served || []).map((s) => (
                    <Badge key={s} variant="outline" className="font-mono text-[10px] border-erf-gold/40 text-erf-dark">{s}</Badge>
                  ))}
                </div>
                <div className="text-xs uppercase font-nav text-erf-dark/70 pt-2">Brand</div>
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded" style={{ background: tenant.brand?.primary_color }} title="gold" />
                  <span className="w-6 h-6 rounded" style={{ background: tenant.brand?.dark_color }} title="dark" />
                  <span className="w-6 h-6 rounded border" style={{ background: tenant.brand?.background_color }} title="cream" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 flex items-center gap-1.5 pt-2">
                  <UsersIcon className="w-3 h-3" /> Paywall {tenant.paywall_immune ? "immune" : "standard"}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminAppShell>
  );
}
